/*
 * @format
 */

import uuid from 'uuid/v4';

import { sendAndWaitForTimeout } from './common';
import * as types from '../action-types';
import * as selectors from '../selectors';
import * as messages from '../web_socket_messages/messages';
import timeout from '../utils/timeout';
import { isCloudSupported, canSettingsBeSent } from '../controls-selectors';
import { getSettingsDifferences } from '../utils/helpers';
import { showDemoUnavailable } from '../utils/demoMode';

const getSiSettingsMessage = setting => {
  // add more cases here when we have more than 1 message
  switch (setting) {
    case 'setWeatherOptimisation':
      return messages.setWeatherOptimisation;
    default:
      return messages.setWeatherOptimisation;
  }
};

export const setPrtSetting =
  (sendWsMessage, homeId, zoneId, prtSetting, value, requestId = uuid()) =>
  async (dispatch, getState) => {
    const state = getState();

    if (selectors.isDemoMode(state)) {
      dispatch(showDemoUnavailable());
      return;
    }

    const deviceToken = selectors.getDeviceToken(state);

    if (!isCloudSupported(state, homeId)) {
      console.log("can't submit setting, cloud not supported");
      return;
    }

    if (!canSettingsBeSent(state, homeId, zoneId)) {
      console.log("can't submit setting, not zone not in allowed mode");
      return;
    }

    dispatch({
      type: types.PRT_SETTINGS_UPDATE_STARTED,
      homeId,
      zoneId,
      requestId,
      prtSetting,
      value,
    });

    const message = messages.getPrtMessage(
      homeId,
      zoneId,
      requestId,
      prtSetting,
      value,
      deviceToken,
    );

    const timeOutAction = {
      type: types.PRT_SETTINGS_UPDATE_TIMED_OUT,
      requestId,
    };

    await dispatch(
      sendAndWaitForTimeout(sendWsMessage, message, timeOutAction),
    );
  };

export const hpiSetPrtSetting =
  (sendWsMessage, homeId, zoneId, prtSetting, value, requestId = uuid()) =>
  async (dispatch, getState) => {
    const state = getState();

    if (selectors.isDemoMode(state)) {
      dispatch(showDemoUnavailable());
      return;
    }

    const deviceToken = selectors.getDeviceToken(state);

    if (!isCloudSupported(state, homeId)) {
      console.log("can't submit setting, cloud not supported");
      return;
    }

    if (
      // TODO: find a better way to handle the unpair route
      prtSetting !== 'hpiUnpairDevice' &&
      !canSettingsBeSent(state, homeId, zoneId)
    ) {
      console.log("can't submit setting, not zone not in allowed mode");
      return;
    }

    dispatch({
      type: types.PRT_SETTINGS_UPDATE_STARTED,
      homeId,
      zoneId,
      requestId,
      prtSetting,
      value,
    });

    const message = messages.hpiGetPrtMessage(
      homeId,
      zoneId,
      requestId,
      prtSetting,
      value,
      deviceToken,
    );

    const timeOutAction = {
      type: types.PRT_SETTINGS_UPDATE_TIMED_OUT,
      requestId,
    };

    await dispatch(
      sendAndWaitForTimeout(sendWsMessage, message, timeOutAction),
    );
  };

export const setInterfaceSetting =
  (sendWsMessage, homeId, settingName, value, requestId = uuid()) =>
  async (dispatch, getState) => {
    if (selectors.isDemoMode(getState())) {
      dispatch(showDemoUnavailable());
      return;
    }

    dispatch({
      type: types.INTERFACE_SETTINGS_UPDATE_STARTED,
      homeId,
      requestId,
      settingName,
      value,
    });

    const message = messages.getInterfaceSettingMessage(
      homeId,
      requestId,
      settingName,
      value,
    );

    const timeOutAction = {
      type: types.INTERFACE_SETTINGS_UPDATE_TIMED_OUT,
      requestId,
    };

    await dispatch(
      sendAndWaitForTimeout(sendWsMessage, message, timeOutAction),
    );
  };

export const setCustomPrtProperty =
  (sendWsMessage, homeId, zoneId, value) => async dispatch => {
    const requestId = uuid();

    dispatch({
      type: types.CUSTOM_PROPERTIES_UPDATE_STARTED,
      homeId,
      zoneId: zoneId[0],
      requestId,
      value,
    });

    const message = messages.setPRTCustomProperties(
      zoneId[0],
      homeId,
      requestId,
      value,
    );

    sendWsMessage(message);
    await timeout();

    dispatch({
      type: types.CUSTOM_PROPERTIES_UPDATE_TIMEOUT,
      homeId,
      zoneId: zoneId[0],
      requestId,
    });
  };

export const setSiSetting =
  (
    sendWsMessage,
    homeId,
    setting,
    value,
    onStart,
    onResolve,
    onTimeout,
    onError,
  ) =>
  async dispatch => {
    const requestId = uuid();

    const messageFunction = getSiSettingsMessage(setting);

    dispatch({
      type: types.SI_SETTINGS_UPDATE,
      sendWsMessage,
      message: messageFunction(homeId, value, requestId),
      onStart,
      onResolve,
      onTimeout,
      onError,
    });
  };

export const copyPRTSettings =
  (
    sendWsMessage,
    homeId,
    zoneId,
    prtSettings,
    onStart,
    onResolve,
    onTimeout,
    onError,
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const deviceToken = selectors.getDeviceToken(state);

    const currentPrtSetting = selectors.getAllPrtSettings(
      state,
      homeId,
      zoneId,
    );

    const newSettings = getSettingsDifferences(currentPrtSetting, prtSettings);

    if (!isCloudSupported(state, homeId)) {
      console.log("can't submit setting, cloud not supported");
      return;
    }

    dispatch({
      type: types.PRT_COPY_SETTING_STARTED,
      sendWsMessage,
      homeId,
      zoneId,
      deviceToken,
      newSettings,
      onStart,
      onResolve,
      onTimeout,
      onError,
    });
  };
